"use client";
import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

/* ─────────────────────────────────────────────
   1. Make your services shine (Website)
 ───────────────────────────────────────────── */
export function MktgServicesShine({ section }) {
  return (
    <section className="overflow-visible py-20 bg-transparent text-white relative font-sans w-full z-10">
      {/* Decorative grey background shape (x_wd_shape x_wd_shape_short) */}
      <div className="absolute right-0 top-[25%] bottom-[10%] w-[35%] md:w-[35%] lg:w-[38%] bg-white/[0.03] rounded-l-[100px] md:rounded-l-[150px] lg:rounded-l-[200px] -z-0 pointer-events-none select-none" />

      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        
        {/* Title and Descriptions Row */}
        <div className="row pb-5 g-4">
          <div className="col-lg-10 d-flex align-items-center gap-3">
            <h2 className={`display-2 mb-5 text-white font-bold tracking-tight leading-tight ${caveat.className}`}>
              Make your services shine
            </h2>
            <Image 
              src="/odoo/graphics/corner_4.svg" 
              width={80} 
              height={80} 
              className="d-none d-lg-block rotate-90 mt-n7 select-none pointer-events-none w-20 h-auto" 
              alt="" 
            />
          </div>
          
          <div className="col-lg-4">
            <p className="mb-4 text-gray-300 leading-relaxed text-xl">
              <strong className="text-white font-bold">Create a stunning website</strong> using Odoo's AI-based builder, 3+ million free photos, drag-and-drop elements, and SEO optimization tools. No coding skills required.
            </p>
          </div>
          
          <div className="col-lg-4">
            <p className="mb-0 text-gray-300 leading-relaxed text-xl">
              <strong className="text-white font-bold">Use templates</strong> to add showstopping pages that gather leads for you. Allow clients to book appointments, consult pricing, and understand your vision at a glance.
            </p>
          </div>
        </div>

        {/* Mockups Row */}
        <div className="row align-items-center justify-content-center mt-lg-n7 py-3 relative z-10">
          
          {/* Desktop/Laptop Mockup */}
          <div className="col-md-9 col-lg-8 position-relative">
            {/* Green pointer doodle */}
            <Image 
              src="/odoo/graphics/pointer_green.svg" 
              width={100} 
              height={100} 
              className="d-none d-md-block position-absolute bottom-0 start-0 flip mb-n2 me-n4 select-none pointer-events-none w-[100px] h-auto z-20" 
              style={{ transform: "scaleX(-1)" }}
              alt="" 
            />
            <Image 
              src="/odoo/industries/marketing-agency/marketing-website-services.webp" 
              width={800}
              height={500}
              className="mx-auto rounded-3xl shadow-2xl hover:scale-[1.01] transition-transform duration-300 w-full h-auto" 
              alt="Marketing Website Desktop" 
            />
          </div>
          
          {/* Mobile Mockup */}
          <div className="col-8 col-sm-6 col-md-3 offset-lg-1">
            <Image 
              src="/odoo/industries/marketing-agency/marketing-website-mobile.webp" 
              width={300}
              height={500}
              className="mx-auto mt-4 mt-md-0 rounded-3xl shadow-2xl hover:scale-[1.01] transition-transform duration-300 w-full h-auto" 
              alt="Marketing Website Mobile" 
            />
          </div>

        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   2. All-in-one project hub
───────────────────────────────────────────── */
export function MktgProjectHub({ section }) {
  return (
    <section className="overflow-visible py-20 bg-transparent text-white relative font-sans w-full z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        
        {/* Header Block with offsets */}
        <div className="row pb-5">
          <div className="col-lg-10 offset-lg-2">
            <h2 className={`display-2 mb-5 text-white font-bold tracking-tight leading-tight ${caveat.className}`}>
              <span className="relative inline-block mr-3">
                <span className="relative z-10 text-white">All-in-one</span>
                <svg className="absolute -bottom-2 left-0 w-full h-3 text-cyan-400 overflow-visible opacity-90" viewBox="0 0 100 10" preserveAspectRatio="none">
                  <path d="M 2 8 C 30 4, 70 3, 98 5 C 75 7, 35 9, 5 8" stroke="currentColor" strokeWidth="4.5" fill="none" strokeLinecap="round" />
                </svg>
              </span>
              project hub
            </h2>
          </div>
          <div className="col-lg-6 offset-lg-2">
            <p className="mb-4 mb-lg-6 text-gray-300 leading-relaxed text-xl">
              <strong className="text-white font-bold">Manage all aspects of your campaigns:</strong> tasks, timesheets, exchanges with clients, meetings, invoices, and even expenses; everything is centralized in one, intuitive dashboard.
            </p>
          </div>
        </div>

        {/* Visual Mockups & Doodles Container */}
        <div className="row align-items-center justify-content-center g-4 mt-4">
          
          {/* First Row: Project Dashboard + Related Records & Dashboard Labels */}
          <div className="col-10 d-flex flex-column flex-lg-row justify-content-center align-items-center gap-4">
            
            {/* Left Doodle Label: Related Records */}
            <em className={`x_wd_doodle me-lg-4 text-purple-300 text-xl font-normal not-italic ${caveat.className} text-center lg:text-right flex flex-col items-center lg:items-end`}>
              Related records
              <Image 
                src="/odoo/arrows/grey_arrow_sm_02.svg" 
                width={20} 
                height={20} 
                className="d-block filter brightness-0 invert opacity-60 mt-1" 
                style={{ transform: "rotate(300deg)", height: "auto", width: "20px" }}
                alt="" 
              />
            </em>

            {/* Main Project App Mockup */}
            <Image 
              src="/odoo/industries/marketing-agency/project.webp" 
              width={900}
              height={600}
              alt="Project app" 
              className="w-full lg:w-[75%] rounded-3xl shadow-2xl hover:scale-[1.01] transition-transform duration-300 h-auto" 
            />

            {/* Right Doodle Label: Project Dashboard */}
            <em className={`x_wd_doodle ms-lg-4 align-self-center text-purple-300 text-xl font-normal not-italic ${caveat.className} text-center lg:text-left flex flex-col items-center lg:items-start`}>
              <Image 
                src="/odoo/arrows/grey_arrow_sm_03.svg" 
                width={20} 
                height={20} 
                className="d-block filter brightness-0 invert opacity-60 mb-1" 
                style={{ transform: "rotate(120deg)", height: "auto", width: "20px" }}
                alt="" 
              />
              Project dashboard
            </em>

          </div>

          {/* Second Row: Timesheet Mockup + Timesheets Label */}
          <div className="col-8 d-flex flex-column flex-lg-row justify-content-center align-items-center gap-4 ms-lg-5 mt-lg-n4">
            
            {/* Timesheets Mockup */}
            <Image 
              src="/odoo/industries/marketing-agency/timesheets.webp" 
              width={600}
              height={400}
              alt="Timesheets" 
              className="w-full lg:w-[50%] rounded-3xl shadow-2xl hover:scale-[1.01] transition-transform duration-300 h-auto" 
            />

            {/* Timesheets Doodle Label */}
            <em className={`x_wd_doodle ms-lg-4 mb-3 align-self-lg-end text-purple-300 text-xl font-normal not-italic ${caveat.className} text-center lg:text-left flex flex-col items-center lg:items-start`}>
              Timesheets
              <Image 
                src="/odoo/arrows/grey_arrow_sm_02.svg" 
                width={20} 
                height={20} 
                className="d-block filter brightness-0 invert opacity-60 mt-1" 
                style={{ transform: "scaleX(-1) rotate(-50deg)", height: "auto", width: "20px" }}
                alt="" 
              />
            </em>

          </div>
        </div>
      </div>
    </section>
  );
}

export function MktgLeadToInvoice({ section }) {
  return (
    <section className="overflow-visible py-20 bg-transparent text-white relative font-sans w-full z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        <div className="row align-items-center">
          
          {/* Left Column: Content */}
          <div className="col-lg-6 col-xl-5 py-4">
            <h2 className={`display-2 mb-5 text-white font-bold tracking-tight leading-tight ${caveat.className}`}>
              From lead to{" "}
              <span className="relative inline-block mr-1">
                <span className="relative z-10 text-white">invoice</span>
                {/* Gold/Orange handdrawn underline doodle */}
                <svg className="absolute -bottom-2 left-0 w-full h-3 text-[#f1833b] overflow-visible opacity-95" viewBox="0 0 100 10" preserveAspectRatio="none">
                  <path d="M 2 8 C 30 4, 70 3, 98 5 C 75 7, 35 9, 5 8" stroke="currentColor" strokeWidth="4.5" fill="none" strokeLinecap="round" />
                </svg>
              </span>
            </h2>
            <div className="space-y-4">
              <p className="text-xl text-gray-300 leading-relaxed">
                <strong className="text-white font-bold">Centralized leads.</strong> Odoo's CRM pipeline allows you to capture and nurture every lead, whether it comes from your website, email, WhatsApp, or else.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed">
                <strong className="text-white font-bold">Flexible invoicing:</strong> send invoices to clients in one click, using their preferred method: email, snail mail, e-invoicing, or a QR code.
              </p>
            </div>
          </div>

          {/* Right Column: CRM Mockup */}
          <div className="col-lg-6 offset-xl-1 py-4 py-md-7 py-lg-0 relative flex justify-center items-center">
            {/* Sharp, visible solid background circle behind mockup matching Odoo's x_wd_shape_circle */}
            <div className="absolute w-[450px] md:w-[500px] lg:w-[600px] h-[450px] md:h-[500px] lg:h-[600px] bg-white/[0.03] rounded-full -z-10 pointer-events-none select-none top-1/2 left-1/2 -translate-x-[45%] -translate-y-1/2" />
            
            {/* Sparkle doodle in Odoo orange with 4 rays */}
            <div className="absolute -top-8 -right-8 w-20 h-20 pointer-events-none select-none z-20">
              <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-[#f1833b] opacity-95">
                <path d="M 30 75 C 28 55, 24 35, 18 15" stroke="currentColor" strokeWidth="6.5" strokeLinecap="round" />
                <path d="M 40 70 C 44 50, 48 30, 52 10" stroke="currentColor" strokeWidth="6.5" strokeLinecap="round" />
                <path d="M 50 72 C 60 55, 70 38, 80 20" stroke="currentColor" strokeWidth="6.5" strokeLinecap="round" />
                <path d="M 58 75 C 72 65, 85 55, 98 45" stroke="currentColor" strokeWidth="6.5" strokeLinecap="round" />
              </svg>
            </div>

            <Image
              src="/odoo/industries/marketing-agency/marketing-crm.webp"
              width={800}
              height={550}
              className="mx-auto rounded-3xl shadow-2xl hover:scale-[1.01] transition-transform duration-300 w-full h-auto z-10 relative"
              alt="Leads"
            />
          </div>

        </div>
      </div>
    </section>
  );
}

export function MktgExpandGrow({ section }) {
  return (
    <section className="overflow-visible py-20 bg-transparent text-white relative font-sans w-full z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        <div className="row">
          
          {/* Left Column: Stacked images */}
          <div className="col-md-10 offset-md-1 col-lg-6 offset-lg-0 order-2 order-lg-1 pb-5 relative min-h-[350px] lg:min-h-[500px] flex items-center justify-center">
            {/* Dome shape background decoration */}
            <div className="absolute top-0 bottom-0 left-[-15%] right-[10%] bg-white/[0.03] rounded-r-[240px] -z-10 pointer-events-none select-none" />
            
            {/* Social marketing main mockup */}
            <Image
              src="/odoo/industries/marketing-agency/social-marketing.webp"
              width={700}
              height={480}
              className="mx-auto rounded-3xl border border-white/10 shadow-2xl z-20 relative lg:absolute lg:bottom-0 lg:left-0 lg:w-[85%] hover:scale-[1.01] transition-transform duration-300 w-full h-auto"
              alt="Social Marketing"
            />

            {/* Events - floating top-left overlay */}
            <Image
              src="/odoo/industries/marketing-agency/events.webp"
              width={400}
              height={260}
              className="hidden lg:block rounded-3xl border border-white/10 shadow-2xl absolute lg:top-4 lg:left-[-5%] lg:w-[50%] z-10 hover:scale-[1.02] transition-transform duration-300"
              style={{ transform: "rotate(-10deg)" }}
              alt="Events"
            />

            {/* Target doodle */}
            <Image
              src="/odoo/icons/red_target.svg"
              width={80}
              height={80}
              className="hidden lg:block absolute top-[40%] right-[10%] -translate-y-1/2 z-30 w-20 h-auto select-none pointer-events-none"
              style={{ filter: "hue-rotate(25deg) saturate(1.4)" }}
              alt="Target"
            />
          </div>

          {/* Right Column: Content */}
          <div className="col-lg-6 col-xl-5 offset-xl-1 order-1 order-lg-2 pb-5 py-lg-8 flex flex-col justify-content-center items-start">
            <h2 className={`display-2 mb-2 text-white font-bold tracking-tight leading-tight ${caveat.className}`}>
              Expand as you grow
            </h2>
            <Image 
              src="/odoo/icons/red_trend-up.svg" 
              width={80} 
              height={80} 
              className="w-20 h-auto select-none pointer-events-none mb-6" 
              style={{ filter: "hue-rotate(25deg) saturate(1.4)" }}
              alt="" 
            />
            <p className="mb-0 text-gray-300 leading-relaxed text-xl">
              <strong className="text-white font-bold">Marketing powerhouse.</strong> Odoo's marketing apps allow you to centralize all your socials in one platform, automate scheduling, create events, and track the results of each campaign.
            </p>
          </div>

        </div>
      </div>
    </section>
  );
}
